import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Space, Tag, Alert, InputNumber, Popconfirm } from 'antd';
import { ToolOutlined, ReloadOutlined } from '@ant-design/icons';
import { getUnattributedCustomers, repairUnresolvedAttributions } from '../../services/referral/marketerService';
import { NotificationManager } from 'react-notifications';

const UnattributedQueueModal = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [repairLoading, setRepairLoading] = useState(false);
  const [unattributedList, setUnattributedList] = useState([]);
  const [repairLimit, setRepairLimit] = useState(100);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchUnattributed = async (page = 1, size = 10) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUnattributedCustomers({ pageNo: page - 1, pageSize: size });
      const resData = res?.data?.data || res?.data || res;
      const content = resData?.unattributed || resData?.content || (Array.isArray(resData) ? resData : []);
      const total = resData?.totalUnattributed ?? resData?.totalElements ?? content.length;

      setUnattributedList(content);
      setPagination({ current: page, pageSize: size, total });
    } catch (err) {
      setError(err?.message || 'Failed to fetch repair queue / unattributed customers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchUnattributed(1, pagination.pageSize);
    }
  }, [visible]);

  const handleRepair = async () => {
    setRepairLoading(true);
    try {
      const res = await repairUnresolvedAttributions(repairLimit);
      if (res?.status === true || res?.success || res?.message?.toLowerCase().includes('success')) {
        NotificationManager.success(res?.message || 'Repair job executed successfully');
        fetchUnattributed(1, pagination.pageSize);
      } else {
        NotificationManager.error(res?.message || 'Repair job failed');
      }
    } catch (err) {
      NotificationManager.error(err?.message || 'An error occurred while repairing attributions');
    } finally {
      setRepairLoading(false);
    }
  };

  const columns = [
    {
      title: 'Customer User ID',
      dataIndex: 'customerUserId',
      key: 'customerUserId',
      render: (val, record) => val || record.userId || 'N/A',
    },
    {
      title: 'Customer Name',
      dataIndex: 'name',
      key: 'name',
      render: (val, record) => val || record.fullName || 'N/A',
    },
    {
      title: 'Email / Phone',
      key: 'contact',
      render: (_, record) => record.email || record.phoneNumber || 'N/A',
    },
    {
      title: 'Support Code',
      dataIndex: 'supportCode',
      key: 'supportCode',
      render: (val) => <Tag color="volcano">{val || 'N/A'}</Tag>,
    },
    {
      title: 'Date Registered',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val) => val || 'N/A',
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ToolOutlined style={{ color: '#FF4B01' }} />
          <span>Repair Queue: Unattributed Customers</span>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {error && <Alert message={error} type="error" showIcon />}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', padding: '12px 16px', borderRadius: '8px' }}>
          <Space>
            <span>Batch Repair Limit:</span>
            <InputNumber min={1} max={500} value={repairLimit} onChange={setRepairLimit} />
          </Space>

          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => fetchUnattributed(pagination.current, pagination.pageSize)} loading={loading}>
              Refresh Queue
            </Button>
            <Popconfirm
              title={`Trigger repair job for up to ${repairLimit} unresolved codes?`}
              onConfirm={handleRepair}
              okText="Execute Repair"
              cancelText="Cancel"
            >
              <Button type="primary" icon={<ToolOutlined />} loading={repairLoading} style={{ backgroundColor: '#FF4B01', borderColor: '#FF4B01' }}>
                Run Repair Job
              </Button>
            </Popconfirm>
          </Space>
        </div>

        <Table
          loading={loading}
          dataSource={unattributedList}
          columns={columns}
          rowKey={(record, idx) => record.customerUserId || record.userId || idx}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => fetchUnattributed(page, pageSize),
          }}
          size="small"
        />
      </Space>
    </Modal>
  );
};

export default UnattributedQueueModal;
